import { ColDef } from 'ag-grid-enterprise'
import { GridContext } from '../../../shared/hooks/useGridContext'
import { CellExportMetadataProvider } from '../../../shared/utils/excelJsExport/excelExportTypes'
import { buildNumberFormat, COLUMN_TYPE_BG_COLORS } from '../../../shared/utils/excelNumberFormat'
import { EnhancedStatementRow } from './dataEnhancement'
import { StatementColumn } from '../types'
import { buildStatementNumberFormat } from './columnUtils'

interface StatementColDefLike extends ColDef<EnhancedStatementRow, any> {
  context?: { column?: StatementColumn }
}

/**
 * Cell metadata provider for the financial statements ExcelJS export
 * (income statement + balance sheet). Reuses `buildStatementNumberFormat` —
 * the same function driving the on-screen `statementValueFormatter` — so
 * screen and Excel export can never drift out of sync, and reuses
 * `api.getCellValue` (falls back to `colDef.field` when a column has no
 * `valueGetter`, e.g. the auto-group name column) instead of duplicating
 * value-extraction logic.
 *
 * Closes over the live `GridContext` (captured at export-click time) so it
 * sees the current `numberFormat`/`showInput` toolbar state.
 */
export const createFinancialStatementCellMetadataProvider = (context: GridContext): CellExportMetadataProvider => {
  return (node, column, api) => {
    const colDef = column.getColDef() as StatementColDefLike
    const data = node.data as EnhancedStatementRow | undefined
    const statementColumn = colDef.context?.column

    const rawValue = api?.getCellValue({ rowNode: node, colKey: column })

    const bold = data?.isParent === true
    const bgColor = statementColumn?.className ? COLUMN_TYPE_BG_COLORS[statementColumn.className] : undefined

    if (colDef.cellDataType !== 'number') {
      return { value: rawValue, bold, align: 'left', bgColor }
    }

    const showInput = context?.showInput ?? false
    const { numberFormat, isPercentage } = buildStatementNumberFormat(
      data,
      statementColumn,
      showInput,
      context?.numberFormat
    )

    return {
      value: typeof rawValue === 'number' ? rawValue : undefined,
      numberFormat: buildNumberFormat(numberFormat, isPercentage),
      bold,
      align: 'right',
      bgColor
    }
  }
}
